import { useCallback, useState } from "react";
import { getLocale } from "../locales";
import { storageService } from "../services/storageService";

const noop = () => {};
const defaultMessages = getLocale().notifications;

export default function useAuth({ onAuthenticated = noop, onLogout = noop, showNotification = noop, t = {} } = {}) {
  const [currentUser, setCurrentUser] = useState(() => storageService.getCurrentUser());
  const [isAuthLoading, setAuthLoading] = useState(false);

  const updateUser = useCallback((userOrUpdater) => {
    setCurrentUser((user) => (typeof userOrUpdater === "function" ? userOrUpdater(user) : userOrUpdater));
  }, []);

  const login = useCallback(
    async (loginData) => {
      setAuthLoading(true);

      try {
        const user = await storageService.login(loginData.email, loginData.password);
        const userMessages = getLocale(user.language).notifications;
        setCurrentUser(user);
        onAuthenticated(user);
        showNotification("success", userMessages.welcomeBack || t.welcomeBack || defaultMessages.welcomeBack);
      } catch (error) {
        showNotification("error", error.message);
      } finally {
        setAuthLoading(false);
      }
    },
    [onAuthenticated, showNotification, t.welcomeBack]
  );

  const register = useCallback(
    async (registerData) => {
      if (registerData.password.length < 8) {
        showNotification("error", t.passwordTooShort || defaultMessages.passwordTooShort);
        return;
      }

      if (registerData.password !== registerData.confirmPassword) {
        showNotification("error", t.passwordsDoNotMatch || defaultMessages.passwordsDoNotMatch);
        return;
      }

      setAuthLoading(true);

      try {
        const user = await storageService.register(registerData);
        const userMessages = getLocale(user.language).notifications;
        setCurrentUser(user);
        onAuthenticated(user);
        showNotification("success", userMessages.accountCreated || t.accountCreated || defaultMessages.accountCreated);
      } catch (error) {
        showNotification("error", error.message);
      } finally {
        setAuthLoading(false);
      }
    },
    [onAuthenticated, showNotification, t.accountCreated, t.passwordsDoNotMatch, t.passwordTooShort]
  );

  const logout = useCallback(() => {
    storageService.clearCurrentUser();
    setCurrentUser(null);
    onLogout();
  }, [onLogout]);

  return {
    currentUser,
    isAuthLoading,
    login,
    logout,
    register,
    updateUser
  };
}
